import { parseDocument } from "htmlparser2";
import { isTag, type AnyNode, type Element as DomElement } from "domhandler";
import { findOne, getInnerHTML } from "domutils";

/**
 * Inline style properties that pin light-theme colors (or opaque backgrounds)
 * and override Tailwind prose / dark mode.
 */
const BLOCKED_STYLE_PROPS = new Set([
  "color",
  "background-color",
  "background",
  "-webkit-text-fill-color",
]);

/**
 * Removes color / background painting declarations from a CSS `style` attribute value.
 * Safe string-only transform (no HTML regex).
 */
export function stripColorAndBackgroundFromStyle(style: string): string {
  if (!style?.trim()) return "";
  return style
    .split(";")
    .map((s) => s.trim())
    .filter(Boolean)
    .filter((decl) => {
      const i = decl.indexOf(":");
      if (i === -1) return true;
      const prop = decl.slice(0, i).trim().toLowerCase();
      return !BLOCKED_STYLE_PROPS.has(prop);
    })
    .join("; ")
    .trim();
}

function walkStripStyleNodes(node: AnyNode): void {
  if (!isTag(node)) return;
  const el = node as DomElement;
  if (el.attribs?.style) {
    const next = stripColorAndBackgroundFromStyle(el.attribs.style);
    if (next) el.attribs.style = next;
    else delete el.attribs.style;
  }
  for (const child of el.children || []) walkStripStyleNodes(child);
}

/** SSR-safe tree walk (htmlparser2 + domutils). */
function cleanQuillHtmlWithDomTree(html: string): string {
  const doc = parseDocument(html);
  const body = findOne(
    (elem): elem is DomElement => isTag(elem) && elem.name === "body",
    doc,
    true
  );
  if (!body) return html;
  walkStripStyleNodes(body);
  return getInnerHTML(body);
}

/** Browser: DOMParser + live style object (matches how Quill serializes). */
function cleanQuillHtmlWithDomParser(html: string): string {
  const tpl = document.createElement("template");
  tpl.innerHTML = html;
  tpl.content.querySelectorAll("[style]").forEach((el) => {
    const next = stripColorAndBackgroundFromStyle(el.getAttribute("style") || "");
    if (next) el.setAttribute("style", next);
    else el.removeAttribute("style");
  });
  return Array.from(tpl.content.childNodes)
    .map((n) => {
      if (n instanceof Element) return n.outerHTML;
      if (n.nodeType === Node.TEXT_NODE) return n.textContent ?? "";
      return "";
    })
    .join("");
}

/**
 * Strips inline `color` / `background` painting from Quill (or pasted) HTML so
 * theme + `prose` / `dark:prose-invert` control colors.
 */
export function cleanQuillHtml(html: string): string {
  if (!html?.trim()) return html;
  try {
    if (typeof document !== "undefined") {
      return cleanQuillHtmlWithDomParser(html);
    }
  } catch {
    // malformed fragment in edge browsers
  }
  return cleanQuillHtmlWithDomTree(html);
}
